import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience } from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { useMemo } from 'react';
import { useMappedLoadingValue } from '../../../../../services/firebase';
import { getUserActorForKey } from '../../../../../services/experiences/instance';
import { toFullName, useUserMapById } from '../../../../../services/user';
import { Issue } from './types';

type ActorRef = { instance: Doc<IExperience>; actorKey: string };

/** Pulls the `(instance, actorKey)` pair a group's copy would resolve a
 * `{{ fullName }}` for, reusing the same per-family fields each `newCopy`
 * entry already reads off `group[0]`. Families that never call
 * `personLabel` (ASSIGNEE_BOUNCE, MISSING_DATA) resolve to `undefined`. */
const actorRefFor = (issue: Issue): ActorRef | undefined => {
  const raw = issue.raw;
  if (raw.family === 'AUTOMATION') {
    const actorKey = (raw.action as { errorData?: { data?: { actorKey?: string } } }).errorData?.data?.actorKey;
    return raw.instance && actorKey ? { instance: raw.instance, actorKey } : undefined;
  }
  if (raw.family === 'SENT_ITEM') return raw.data.actor ? { instance: raw.data.instance, actorKey: raw.data.actor } : undefined;
  if (raw.family === 'CC') return { instance: raw.data.instance, actorKey: raw.data.recipientId };
  if (raw.family === 'INSTANCE_SUBTASK') {
    const data = raw.data as { instance: Doc<IExperience>; actorKey?: string };
    return data.actorKey ? { instance: data.instance, actorKey: data.actorKey } : undefined;
  }
  return undefined;
};

const keyFor = (ref: ActorRef): string => `${ref.instance.id}:${ref.actorKey}`;

/** One batched `useUserMapById` call across every group currently on
 * screen, sized to the number of groups shown rather than to how many
 * actors each instance has. Feeds `IssueCopyContext.resolveActorFullName`. */
export const useResolveGroupActorNames = (orgId: string, groups: Issue[][]): { [key: string]: string } => {
  const refs = useMemo(() => groups.map((g) => actorRefFor(g[0])).filter((r): r is ActorRef => !!r), [groups]);

  const userIds = useMemo(() => {
    const ids = new Set<string>();
    refs.forEach((ref) => {
      const userId = getUserActorForKey(ref.instance, ref.actorKey)?.userId;
      if (userId) ids.add(userId);
    });
    return Array.from(ids);
  }, [refs]);

  const toNamesByKey = useMemo(
    () => (usersById: { [id: string]: Doc<IUser> }) => {
      const result: { [key: string]: string } = {};
      refs.forEach((ref) => {
        const userId = getUserActorForKey(ref.instance, ref.actorKey)?.userId;
        const user = userId ? usersById[userId] : undefined;
        if (user) result[keyFor(ref)] = toFullName(user.data);
      });
      return result;
    },
    [refs]
  );

  const [namesByKey] = useMappedLoadingValue(useUserMapById(orgId, userIds), toNamesByKey, true);
  return namesByKey || {};
};

/** Builds `IssueCopyContext.resolveActorFullName` from the map above. */
export const actorFullNameResolver =
  (namesByKey: { [key: string]: string }) =>
  (actorKey: string, instance: Doc<IExperience> | undefined): string | undefined =>
    instance ? namesByKey[`${instance.id}:${actorKey}`] : undefined;
